class CartItems extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', this.onChange.bind(this));
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate(event) {
    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target);
      return;
    }

    this.updateCartSections(event);
    this.handleFocusManagement(event);
    
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        cart: event.cart
      }
    }));
  }

  updateCartSections(event) {
    const sectionToRender = new DOMParser().parseFromString(event.cart.sections[this.sectionId], 'text/html');

    // Update mini cart if exists
    const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);
    if (miniCart) {
      const updatedElement = sectionToRender.querySelector(`#MiniCart-${this.sectionId}`);
      if (updatedElement) {
        miniCart.innerHTML = updatedElement.innerHTML;
      }
    }

    // Update main cart if exists
    const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${this.sectionId}`);
      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
      }
      else {
        mainCart.closest('.cart').classList.add('is-empty');
        mainCart.remove();
      }
    }
  }

  handleFocusManagement(event) {
    const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
    const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
    const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);

    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      theme.a11y.trapFocus(mainCart || miniCart, lineItem.querySelector(`[name="${event.name}"]`));
    }
    else if (event.cart.item_count === 0) {
      miniCart
        ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('a'))
        : theme.a11y.trapFocus(document.querySelector('.empty-state'), document.querySelector('.empty-state__link'));
    }
    else {
      miniCart
        ? theme.a11y.trapFocus(miniCart, miniCart.querySelector('.horizontal-product__title'))
        : theme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart__item-title'));
    }
  }

  onCartError(errors, target) {
    if (target) {
      this.disableLoading(target.getAttribute('data-index'));
      this.setValidity(target, errors);
      return;
    }
    window.location.href = theme.routes.cart_url;
  }

  updateQuantity(line, quantity, name, target) {
    this.enableLoading(line);

    // Get sections that need to be re-rendered (including bundle sections)
    const sectionsToBundle = this.getSectionsToBundle();

    const body = JSON.stringify({
      id: line,
      quantity,
      sections: sectionsToBundle,
      // Add bundle-specific properties if needed
      properties: target.closest('[data-bundle]') ? { 
        _bundle: true,
        _bundle_id: target.closest('[data-bundle]').dataset.bundleId
      } : {}
    });

    fetch(`${theme.routes.cart_change_url}`, { ...theme.utils.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { 
          source: 'cart-items', 
          cart: parsedState, 
          target, 
          line, 
          name 
        });
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Cart update error:', error);
        }
      });
  }

  getSectionsToBundle() {
    const sections = [];
    // Dispatch event to collect all sections that need updating
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { 
      bubbles: true, 
      detail: { sections } 
    }));
    return sections;
  }

  enableLoading(line) {
    const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
    if (loader) loader.hidden = false;
  }

  disableLoading(line) {
    const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
    if (loader) loader.hidden = true;
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  validateQuantity(event) {
    const target = event.target;
    const inputValue = parseInt(target.value);
    const index = target.getAttribute('data-index');
    let message = '';

    if (inputValue < parseInt(target.getAttribute('data-min'))) {
      message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
    }
    else if (inputValue > parseInt(target.max)) {
      message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
    }
    else if (inputValue % parseInt(target.step) !== 0) {
      message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
    }

    if (message) {
      this.setValidity(target, message);
    }
    else {
      target.setCustomValidity('');
      target.reportValidity();
      this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
    }
  }
}
customElements.define('cart-items', CartItems);



class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } });
  }
}
customElements.define('cart-note', CartNote);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  onPrepareBundledSections(event) {
    event.detail.sections.push(this.sectionId);
  }
}
customElements.define('main-cart', MainCart);

class CountryProvince extends HTMLElement {
  constructor() {
    super();

    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));

    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
      this.countryElement.dispatchEvent(new Event('change'));
    }
    else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute('data-provinces'));
    this.provinceElement.parentElement.hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.getAttribute('data-province');
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvince);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.onSubmitHandler = this.onSubmit.bind(this);
  }

  connectedCallback() {
    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.onSubmitHandler);
  }

  disconnectedCallback() {
    this.submitButton.removeEventListener('click', this.onSubmitHandler);
  }

  onSubmit(event) {
    event.preventDefault();

    this.abortController?.abort();
    this.abortController = new AbortController();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.setAttribute('aria-busy', 'true');

    const body = JSON.stringify({
      shipping_address: { zip, country, province }
    });
    let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

    // remove double `/` in case shop might have /en or language in URL
    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...theme.utils.fetchConfig('javascript'), ...{ body }, signal: this.abortController.signal })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        }
        else {
          this.formatError(parsedState);
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        }
        else {
          console.error(error);
        }
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.removeAttribute('aria-busy');
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error grid gap-2 text-sm leading-tight">
        <p>${theme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--${shippingRates.length === 0 ? 'error' : 'success'} grid gap-2 text-sm leading-tight">
        <p>${shippingRates.length === 0 ? theme.shippingCalculatorStrings.notFound : shippingRates.length === 1 ? theme.shippingCalculatorStrings.oneResult : theme.shippingCalculatorStrings.multipleResults}</p>
        ${shippingRatesList === '' ? '' : `<ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>`}
      </div>
    `;

  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });
